// Dev-only frame profiler. Enable with ?debug=perf in the URL.

type SegmentStats = {
  totalMs: number;
  count: number;
  lastMs: number;
};

const segments = new Map<string, SegmentStats>();
let enabled = false;
let overlayEl: HTMLDivElement | null = null;
let frameStart = 0;
let frameCount = 0;
let fpsLast = 0;
let fpsTimer = 0;

export function initProfiler(): void {
  const params = new URLSearchParams(window.location.search);
  enabled = params.has('debug') && params.get('debug') === 'perf';
  if (!enabled) return;

  overlayEl = document.createElement('div');
  Object.assign(overlayEl.style, {
    position: 'fixed',
    top: 'max(4px, env(safe-area-inset-top, 0px))',
    left: 'max(4px, env(safe-area-inset-left, 0px))',
    zIndex: '100',
    padding: '6px 8px',
    background: 'rgba(0,0,0,0.72)',
    color: '#8f8',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: '1.45',
    whiteSpace: 'pre',
    pointerEvents: 'none',
    userSelect: 'none',
  } as CSSStyleDeclaration);
  document.body.appendChild(overlayEl);
  requestAnimationFrame(tickOverlay);
}

function tickOverlay(now: number): void {
  if (!enabled || !overlayEl) return;
  if (fpsTimer === 0) fpsTimer = now;
  const elapsed = now - fpsTimer;
  if (elapsed >= 500) {
    fpsLast = (frameCount * 1000) / elapsed;
    frameCount = 0;
    fpsTimer = now;
    renderOverlay();
  }
  requestAnimationFrame(tickOverlay);
}

function renderOverlay(): void {
  if (!overlayEl) return;
  const lines = [`FPS ${fpsLast.toFixed(1)}`];
  for (const [name, s] of [...segments.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    const avg = s.count > 0 ? s.totalMs / s.count : 0;
    lines.push(`${name}: ${s.lastMs.toFixed(2)}ms (avg ${avg.toFixed(2)})`);
  }
  overlayEl.textContent = lines.join('\n');
}

export function isProfilerEnabled(): boolean {
  return enabled;
}

export function profilerBeginFrame(): void {
  if (!enabled) return;
  frameStart = performance.now();
}

export function profilerEndFrame(): void {
  if (!enabled) return;
  const ms = performance.now() - frameStart;
  record('frame', ms);
  frameCount++;
}

export function profilerMeasure<T>(name: string, fn: () => T): T {
  if (!enabled) return fn();
  const t0 = performance.now();
  try {
    return fn();
  } finally {
    record(name, performance.now() - t0);
  }
}

function record(name: string, ms: number): void {
  let s = segments.get(name);
  if (!s) {
    s = { totalMs: 0, count: 0, lastMs: 0 };
    segments.set(name, s);
  }
  s.lastMs = ms;
  s.totalMs += ms;
  s.count++;
}

/** Snapshot for docs/perf-baseline.md (call from console: __meritousProfilerDump()). */
export function dumpProfilerStats(): Record<string, { lastMs: number; avgMs: number; count: number }> {
  const out: Record<string, { lastMs: number; avgMs: number; count: number }> = {};
  for (const [name, s] of segments) {
    out[name] = {
      lastMs: s.lastMs,
      avgMs: s.count > 0 ? s.totalMs / s.count : 0,
      count: s.count,
    };
  }
  return out;
}

declare global {
  interface Window {
    __meritousProfilerDump?: () => ReturnType<typeof dumpProfilerStats>;
  }
}

if (typeof window !== 'undefined') {
  window.__meritousProfilerDump = dumpProfilerStats;
}
